import apiClient from "../../utils/axiosConfig";
import { API_ENDPOINTS, getApiUrl } from "../../Configurations/config";
import { formatErrorMessage, normalizeApiError } from "../../utils/apiErrors";

/**
 * Passes caller-supplied headers through untouched.
 *
 * Identity headers (`Authorization: Bearer <token>` and `X-User-Email`) are set
 * centrally by the shared Axios request interceptor in `src/utils/axiosConfig.jsx`,
 * so this module no longer keeps a second source of truth for them (Req 4.2).
 */
function semanticHeaders(extra = {}) {
  return { ...extra };
}

/**
 * Rethrows a failed request as a display-safe `Error` (Req 10.1, 10.2).
 *
 * The previous helper flattened the Axios error into `new Error(detail)`, which
 * both leaked whatever the backend put in `detail` and destroyed `error.response`,
 * so no caller could recover the reference ID. This wrapper instead:
 *
 *  - reuses `error.apiError` when the shared interceptor already attached it, and
 *    falls back to `normalizeApiError` for errors raised before the interceptor ran;
 *  - sets `message` to `formatErrorMessage(descriptor)`, so every existing caller
 *    that reads `error.message` gets sanitized text plus "(Reference: <id>)";
 *  - carries the descriptor forward on `apiError` and preserves `response`,
 *    `status`, and `cause`, so a caller can re-normalize without re-parsing.
 *
 * @param {unknown} error the original Axios (or transport) error
 * @returns {Error} the error to throw
 */
function toDisplayError(error) {
  const descriptor =
    error && typeof error === "object" && error.apiError
      ? error.apiError
      : normalizeApiError(error);

  const displayError = new Error(formatErrorMessage(descriptor));
  displayError.name = "SemanticApiError";
  displayError.apiError = descriptor;
  displayError.status = descriptor.status;
  displayError.referenceId = descriptor.referenceId;
  if (error && typeof error === "object") {
    if (error.response !== undefined) displayError.response = error.response;
    if (error.silent === true) displayError.silent = true;
    displayError.cause = error;
  }
  return displayError;
}

async function get(endpoint, config = {}) {
  try {
    const response = await apiClient.get(getApiUrl(endpoint), {
      ...config,
      headers: semanticHeaders(config.headers),
    });
    return response.data;
  } catch (error) {
    throw toDisplayError(error);
  }
}

async function post(endpoint, payload, config = {}) {
  try {
    const response = await apiClient.post(getApiUrl(endpoint), payload, {
      ...config,
      headers: semanticHeaders(config.headers),
    });
    return response.data;
  } catch (error) {
    throw toDisplayError(error);
  }
}

async function put(endpoint, payload, config = {}) {
  try {
    const response = await apiClient.put(getApiUrl(endpoint), payload, {
      ...config,
      headers: semanticHeaders(config.headers),
    });
    return response.data;
  } catch (error) {
    throw toDisplayError(error);
  }
}

async function del(endpoint, config = {}) {
  try {
    const response = await apiClient.delete(getApiUrl(endpoint), {
      ...config,
      headers: semanticHeaders(config.headers),
    });
    return response.data;
  } catch (error) {
    throw toDisplayError(error);
  }
}

function queryString(params) {
  const url = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    url.set(key, String(value));
  });
  const built = url.toString();
  return built ? `?${built}` : "";
}

async function transcribeSpeech(blob, filename = "recording.webm") {
  const formData = new FormData();
  formData.append("file", blob, filename);
  try {
    const response = await apiClient.post(
      getApiUrl(API_ENDPOINTS.SEMANTIC.SPEECH_TRANSCRIBE),
      formData,
      {
        headers: semanticHeaders({ "Content-Type": "multipart/form-data" }),
      }
    );
    return response.data;
  } catch (error) {
    throw toDisplayError(error);
  }
}

export const api = {
  getStatus: () => get(API_ENDPOINTS.SEMANTIC.HEALTH),
  getMetadata: (params = {}) => get(`${API_ENDPOINTS.SEMANTIC.METADATA}${queryString(params)}`),
  getCatalog: (params = {}) => get(`${API_ENDPOINTS.SEMANTIC.METADATA_CATALOG}${queryString(params)}`),
  getSemanticConnectionTest: () => get(API_ENDPOINTS.SEMANTIC.SEMANTIC_CONNECTION_TEST),
  getSynonyms: () => get(API_ENDPOINTS.SEMANTIC.SYNONYMS),
  getBusinessSemantic: () => get(API_ENDPOINTS.SEMANTIC.BUSINESS_SEMANTIC),
  getSuggestions: (q, limit = 8) => get(`${API_ENDPOINTS.SEMANTIC.SUGGESTIONS}${queryString({ q, limit })}`),
  thinking: (payload) => post(API_ENDPOINTS.SEMANTIC.THINKING, payload),
  query: (payload) => post(API_ENDPOINTS.SEMANTIC.QUERY, payload),
  queryRule: (payload) => post(API_ENDPOINTS.SEMANTIC.QUERY_RULE, payload),
  queryAI: (payload) => post(API_ENDPOINTS.SEMANTIC.QUERY_AI, payload),
  transcribeSpeech,
  saveMeasureSynonyms: (payload) => put(API_ENDPOINTS.SEMANTIC.SYNONYMS_MEASURES, payload),
  saveDimensionSynonyms: (payload) => put(API_ENDPOINTS.SEMANTIC.SYNONYMS_DIMENSIONS, payload),
  saveValueSynonyms: (payload) => put(API_ENDPOINTS.SEMANTIC.SYNONYMS_VALUES, payload),
  saveBusinessSemantic: (payload) => put(API_ENDPOINTS.SEMANTIC.BUSINESS_SEMANTIC, payload),

  // ── Session management ─────────────────────────────────────────────────────
  // Sessions are scoped by the authenticated identity (Bearer token + X-User-Email),
  // so no user_id query parameter is sent (Req 4.1, 4.2); limit is retained (Req 4.3).
  listSessions: (limit = 50) =>
    get(`${API_ENDPOINTS.SEMANTIC.SESSIONS}${queryString({ limit })}`),

  getSession: (sessionId, limit = 100) =>
    get(`${API_ENDPOINTS.SEMANTIC.SESSION_DETAIL(sessionId)}${queryString({ limit })}`),

  deleteSession: (sessionId, hard = false) =>
    del(`${API_ENDPOINTS.SEMANTIC.SESSION_DETAIL(sessionId)}${queryString({ hard })}`),

  submitFeedback: (sessionId, messageId, feedback, comment = '') =>
    post(API_ENDPOINTS.SEMANTIC.SESSION_FEEDBACK(sessionId), {
      message_id: messageId,
      feedback,
      ...(comment ? { comment } : {}),
    }),
};
